<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    content: Schemas['CmsElementCallToAction']
}>()

const config = computed(() => props.content?.config)

const requiredFields = computed(() =>
    config.value?.theme?.value != null && config.value?.title?.value != null
)

const hasHeadline = computed(() =>
    config.value?.headline?.value != null && config.value?.headline?.value !== ''
)

const isButtonConfigured = computed(() =>
    config.value?.buttonText?.value != null && config.value?.target?.value != null
)

const alignmentMap = {
    center: { text: 'text-center', items: 'items-center', button: 'self-center' },
    left:   { text: 'text-left',   items: 'items-start',  button: 'self-start' },
    right:  { text: 'text-right',  items: 'items-end',    button: 'self-end' },
} as const

const textAlignment = computed(() => alignmentMap[config.value?.alignment?.value ?? 'center']?.text)
const itemsAlignment = computed(() => alignmentMap[config.value?.alignment?.value ?? 'center']?.items)
const buttonAlignment = computed(() => alignmentMap[config.value?.alignment?.value ?? 'center']?.button)

type ThemeStyle = { background: string; text: string; button: string }

const defaultTheme: ThemeStyle = { background: '#1B421F', text: '#FFFFFF', button: 'btn-accent' }

const themeMap: Record<string, ThemeStyle> = {
    'theme-1': { background: '#F1F0EE', text: '#3A3A3A', button: 'btn-primary' },
    'theme-2': { background: 'transparent', text: '#3A3A3A', button: 'btn-primary' },
    'theme-3': { background: '#B5F678', text: '#3A3A3A', button: 'btn-primary' },
    'theme-4': { background: '#3A3A3A', text: '#FFFFFF', button: 'btn-secondary' },
    'theme-5': { background: '#077A50', text: '#FFFFFF', button: 'btn-secondary' },
    'theme-6': { background: '#A41829', text: '#FFFFFF', button: 'btn-secondary' },
    'theme-7': defaultTheme,
    'theme-8': { background: 'transparent', text: '#3A3A3A', button: '' },
}

const theme = computed(() => themeMap[config.value?.theme?.value ?? ''] ?? defaultTheme)
const isTheme8 = computed(() => config.value?.theme?.value === 'theme-8')
</script>

<template>
    <div
        v-if="requiredFields"
        class="relative flex justify-center w-full"
        :class="[
            itemsAlignment,
            {
                'py-8': config?.isExtraPadding?.value,
                'h-full': !config?.isSmall?.value,
                'h-32': config?.isSmall?.value,
            }
        ]"
        :style="`background-color:${theme.background};`"
    >
        <div
            class="w-full flex flex-col h-full xl:max-w-4xl"
            :class="[
                itemsAlignment,
                config?.isSmall?.value
                    ? 'max-h-32 gap-8 sm:gap-0 p-2 w-full sm:justify-between sm:h-32'
                    : isTheme8 ? '' : 'p-4 lg:p-8',
                hasHeadline ? 'justify-between' : 'justify-start'
            ]"
        >
            <div
                v-if="hasHeadline"
                class="font-semibold text-3xl"
                :class="[textAlignment]"
                :style="`color:${theme.text};`"
            >
                {{ config?.headline?.value }}
            </div>

            <div class="flex flex-col gap-2">
                <div
                    class="font-semibold"
                    :class="[
                        textAlignment,
                        config?.isSmall?.value ? 'text-sm md:text-lg w-1/2 sm:w-full self-end mr-9 sm:mr-0' : 'text-xl mb-2'
                    ]"
                    :style="`color:${theme.text};`"
                >
                    {{ config?.title?.value }}
                </div>

                <div
                    v-if="!config?.isSmall?.value"
                    class="text-sm whitespace-pre-wrap"
                    :class="[textAlignment, { 'mb-6': isButtonConfigured }]"
                    :style="`color:${theme.text};`"
                >
                    {{ config?.subTitle?.value }}
                </div>

                <MiscLink
                    v-if="isButtonConfigured && config?.isButtonTargetMail?.value"
                    :to="`mailto:${config?.target?.value}`"
                    :without-locale="true"
                    class="flex gap-3"
                    :class="[
                        theme.button,
                        buttonAlignment,
                        config?.isSmall?.value ? 'w-full' : '',
                        isTheme8 ? 'items-center text-[#1B421F] text-sm font-semibold' : 'btn pl-5 pr-4 min-w-[241px] text-sm'
                    ]"
                >
                    {{ config?.buttonText?.value }}
                </MiscLink>

                <MiscLink
                    v-if="isButtonConfigured && !config?.isButtonTargetMail?.value"
                    :to="config?.target?.value"
                    class="flex gap-3"
                    :class="[
                        theme.button,
                        buttonAlignment,
                        config?.isSmall?.value ? 'w-full' : '',
                        isTheme8 ? 'items-center text-[#1B421F] text-sm font-semibold' : 'btn pl-5 pr-4 min-w-[241px] text-sm'
                    ]"
                >
                    {{ config?.buttonText?.value }}
                    <MiscIcon name="arrow-right" />
                </MiscLink>
            </div>
        </div>
    </div>
</template>
